import type { Permission } from '@mt/shared';

/**
 * Tài khoản chỉ có vai trò Cộng tác viên (DEC-027/119): không phải nhân viên công ty nên không có chấm công,
 * cổng nhân viên, việc được giao (anh Tiến 11/10/2026, DEC-268). Chỉ là ẩn giao diện; quyền thật vẫn kiểm ở backend.
 */
export const isCtvOnly = (roles: readonly string[] | undefined) => !!roles?.length && roles.every((r) => r === 'ctv');

/**
 * Tài khoản chỉ có vai trò "Nhân viên (chỉ chấm công)" (staff): giao diện điện thoại gọn, chỉ Chấm công / Của tôi / Hướng dẫn
 * (anh Tiến 13/10/2026: "làm lại màn hình cho nhân viên chỉ chấm công thật đơn giản"). Chỉ là ẩn giao diện; quyền thật kiểm ở backend.
 */
/** Quyền mọi NV đều có (thông báo, Bảng tin nội bộ, báo dự án): chỉ có các quyền này = NV chỉ chấm công. */
/** DEC-446: quyền menu (menu.*) chỉ ẩn / hiện mục menu, không tính khi nhận diện NV chỉ chấm công / phụ kho. */
// DEC-640: NV chấm công / phụ kho được tick thêm "Đăng bài lên web" vẫn giữ giao diện gọn (nút Đăng bài ở đầu trang)
const ADDON_PERMS = new Set(['web_post.write']);
const isMenuPerm = (p: string) => p.startsWith('menu.') || ADDON_PERMS.has(p);
const STAFF_BASE_PERMS = new Set(['notification.read', 'feed.use', 'site.report']); // DEC-369: site.report mọi tài khoản đều có

export const isStaffOnly = (roles: readonly string[] | undefined, permissions?: readonly string[]) =>
  (!!roles?.length && roles.every((r) => r === 'staff')) ||
  // Vị trí chép quyền NV chấm công (Phụ xe, Giám sát xưởng…: chỉ có notification.read, feed.use) cũng dùng giao diện gọn (anh Tiến 14/10/2026)
  (!!roles?.length && !isCtvOnly(roles) && !!permissions && permissions.every((p) => STAFF_BASE_PERMS.has(p) || isMenuPerm(p)));

/**
 * Phụ kho (anh Tiến 14/10/2026: "Phụ kho: chấm công, soạn hàng"): quyền chỉ gồm soạn hàng + xem phiếu giao + quyền nền NV (chuông, bảng tin).
 * Mở app vào thẳng Soạn hàng, thanh dưới Soạn hàng / Chấm công / Của tôi. Chỉ là ẩn giao diện; quyền thật kiểm ở backend.
 */
const PICKER_PERMS = new Set(['delivery.pick', 'delivery.read', ...STAFF_BASE_PERMS]);
export const isPickerOnly = (roles: readonly string[] | undefined, permissions?: readonly string[]) =>
  !!roles?.length && !isCtvOnly(roles) && !!permissions?.includes('delivery.pick') && permissions.every((p) => PICKER_PERMS.has(p) || isMenuPerm(p));

/** Vai trò giữ chân trang cũ dù có quyền báo giá / mua hàng (Kế toán; Kiểm soát có ô Đơn Asia riêng từ 21/10/2026). */
const KEEP_OLD_BAR = new Set(['accountant', 'controller']);
/**
 * Chân trang điện thoại 5 mục Báo giá · Đơn Asia · Giao hàng · Lead · Việc của tôi (anh Tiến 02/11/2026):
 * Kinh doanh, Mua hàng, Trưởng bộ phận, Giám đốc khu vực = người lập báo giá hoặc làm mua hàng.
 * Không áp cho CTV, Kế toán / Kiểm soát thuần, thủ kho (có Soạn hàng, không xem báo giá). Chỉ là giao diện; quyền thật kiểm ở backend.
 */
export const usesSalesBar = (roles: readonly string[] | undefined, can: (p: Permission) => boolean) =>
  !!roles?.length &&
  !isCtvOnly(roles) &&
  !roles.every((r) => KEEP_OLD_BAR.has(r)) &&
  !(can('delivery.pick') && !can('quote.read')) &&
  (can('quote.create') || can('purchase.read'));
